'use client'

import { useRef, useState } from 'react'

import Icon from '@/components/Icon/Icon'
import Button from '@/components/Button/Button'
import Input from '@/components/Input/Input'

import { useAuth, AUTH_TYPES } from './AuthContext'

import styles from './Auth.module.scss'
import classNames from 'classnames/bind'
const classes = classNames.bind(styles)

export default function RegisterForm() {
    const { register, setSelectedType, urlParams, externalAuthRequested, externalAuthLoading } = useAuth()
    const emailInput = useRef(null)
    const passwordInput = useRef(null)
    const firstNameInput = useRef(null)
    const lastNameInput = useRef(null)

    const [revealPassword, setRevealPassword] = useState(false)
    const [loading, setLoading] = useState(false)

    const submitRegister = async () => {
        if (loading) return
        setLoading(true)
        try {
            const source = urlParams.source || undefined
            await register(
                emailInput.current.value,
                passwordInput.current.value,
                firstNameInput.current.value,
                lastNameInput.current.value,
                source
            )
        } finally {
            setLoading(false)
        }
    }

    const handlePasswordEyeClick = () => {
        setRevealPassword(!revealPassword)
        setTimeout(() => {
            passwordInput.current.focus()
        }, 50)
    }

    return (
        <>
            <form
                className={classes('form')}
                onSubmit={(e) => {
                    e.preventDefault()
                    submitRegister()
                }}
            >
                <h1 className={classes('title')}>Let's create an account</h1>
                <p className={classes('subtext')}>
                    Your account will allow you to keep all your tax information in one place, easily submit repeat
                    returns and get personalised advice!
                </p>
                <div className={classes('input-container', 'split')}>
                    <Input
                        placeholder="First name"
                        defaultValue={urlParams.firstName || ''}
                        required={true}
                        ref={firstNameInput}
                    />
                    <Input
                        placeholder="Last name"
                        defaultValue={urlParams.lastName || ''}
                        required={true}
                        ref={lastNameInput}
                    />
                </div>
                <div className={classes('input-container')}>
                    <Input
                        type="email"
                        placeholder="Your email"
                        required={true}
                        ref={emailInput}
                        className={classes('icon-input')}
                        autoComplete="username"
                        defaultValue={urlParams.email || ''}
                        disabled={!!urlParams.email}
                    />
                    <div className={classes('error-icon')}>
                        <Icon name="alert-circle" width={20} height={20} />
                    </div>
                </div>
                <div className={classes('input-container')}>
                    <Input
                        type={revealPassword ? 'text' : 'password'}
                        placeholder="Password"
                        title={
                            'Password must have minimum length of 8 characters  and contain at least 1 lowercase character, 1 uppercase letter, 1 number and 1 special character (!@#$%^&*).'
                        }
                        ref={passwordInput}
                        required={true}
                        className={classes('icon-input')}
                        autoComplete="new-password"
                    />
                    <div
                        className={classes('password-reveal', {
                            'password-reveal--active': revealPassword,
                        })}
                        onMouseDown={handlePasswordEyeClick}
                    >
                        {revealPassword && <Icon name="eye" width={20} height={20} />}
                        {!revealPassword && <Icon name="eye-off" width={20} height={20} />}
                    </div>
                    <div className={classes('password-banner')}>
                        Password must have minimum length of 8 characters and contain at least 1 lowercase character, 1
                        uppercase letter, 1 number and 1 special character:
                        <span>{`(^ $ * . [ ] { } ( ) ? - " ! @ # % & / \\ , > < ' : ; | _ ~ \` + =)`}</span>
                    </div>
                </div>

                <Button
                    className={classes('login-button')}
                    text={loading ? 'Creating account...' : 'Create account'}
                    disabled={loading}
                />
            </form>
            <div className={classes('or-separator')}>
                <div className={classes('line')}></div>
                <span>or</span>
                <div className={classes('line')}></div>
            </div>
            <div className={classes('buttons')}>
                <button
                    className={classes('auth-button')}
                    disabled={externalAuthLoading || loading}
                    onClick={externalAuthRequested}
                >
                    <Icon name="google" />
                    <span>{externalAuthLoading ? 'Authenticating...' : 'Sign in with Google'}</span>
                </button>
            </div>
            <span className={classes('login-link')}>
                Already have an account?{' '}
                <a href="#" onClick={() => setSelectedType(AUTH_TYPES.LOGIN)}>
                    Log in
                </a>
            </span>
        </>
    )
}
